/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import * as fromSubmitHighlightForm from '../actions/submit-highlight-form.action';
import { LibraryState } from '../reducers';
import { LibraryService } from 'app/library/services/library.service';
import { EmailsService } from 'app/shared/services/emails.service';
import { Constants } from 'app/shared/models/constants.model';
import { GetLibraryHighlights } from 'app/library/store/actions';

@Injectable()
export class SubmitHighlightFormEffects {
  readonly HR_TYPES = Constants.HIGHLIGHT_REEL_TYPES;

  submitHighlighForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSubmitHighlightForm.SUBMIT_HIGHLIGHT_FORM),
      switchMap((action: fromSubmitHighlightForm.SubmitHighlightForm) => {
        return this.libraryService.submitHighlightForm(this.emptifyHrrNullData(action.payload)).pipe(
          map((response: any) => {
            if (!response.result.success) {
              return new fromSubmitHighlightForm.SubmitHighlightFormFail(response.result.errorMessage);
            }

            if (action.payload.athleteInfo.draft === false) {
              let businessDays;
              const hrPurchase = { ...response.data.purchase, sport: action.payload.purchase?.sport };

              if (
                [Constants.SPORTS.VOLLEYBALL, Constants.SPORTS.BASKETBALL].includes(
                  hrPurchase?.sport?.name?.toLowerCase()
                )
              ) {
                businessDays = Constants.HR_DAYS_TO_DELIVER.VOLLEY_BASKETBALL;
              } else {
                if (hrPurchase && this.libraryService.getHrType(hrPurchase.type) === this.HR_TYPES.PRO) {
                  businessDays = Constants.HR_DAYS_TO_DELIVER.PRO;
                } else {
                  businessDays = Constants.HR_DAYS_TO_DELIVER.STANDARD;
                }
              }

              this.emailService.sendHighlightRequestReelReceivedConfirmation(businessDays).subscribe();
            }

            response.draft = action.payload.athleteInfo.draft;
            response.preventRedirect = action.payload.preventRedirect;
            this.store.dispatch(new GetLibraryHighlights());
            return new fromSubmitHighlightForm.SubmitHighlightFormSuccess(response);
          }),
          catchError((error) => of(new fromSubmitHighlightForm.SubmitHighlightFormFail(error.message)))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private libraryService: LibraryService,
    private emailService: EmailsService,
    private store: Store<LibraryState>
  ) {}

  emptifyHrrNullData(data: any) {
    return {
      ...data,
      athleteInfo: {
        ...data.athleteInfo,
        playerName: data.athleteInfo.playerName || '',
        schoolName: data.athleteInfo.schoolName || '',
        schoolAddress: data.athleteInfo.schoolAddress || '',
        gradYear: data.athleteInfo.gradYear || new Date().getFullYear().toString(),
        positions: data.athleteInfo.positions || '',
        jerseyNumbers: data.athleteInfo.jerseyNumbers || '',
        teamName: data.athleteInfo.teamName || '',
      },
      playsInfo: [
        ...data.playsInfo.map((play) => ({
          ...play,
          jerseyNumber: play.jerseyNumber || '',
          description: play.description || '',
          timecode: play.timecode || '',
        })),
      ],
    };
  }
}
